/**
 * Component for displaying information about mopping and vacuuming.
 *
 * @component
 * @param {Object} data - Data containing information about mopping and vacuuming.
 * @returns {JSX.Element} - MoppingAndVaccuming component.
 */

import React, { useEffect, useRef, useState } from "react";
import Number from "../Number";
import Image from "next/image";
import { motion, useInView, useAnimation } from "framer-motion";
import FurDetectionAnimation from "./FurDetectionAnimation";

import homeIsQuiet from "@assets/images/homepage/home_is_quiet.webp";
import homeCleaningMacros from "@assets/images/homepage/home_cleaning_macros.webp";
import moppingAndVacuuming from "@assets/images/homepage/mopping_and_vacuuming.webp";
import LazyVideo from "../LazyVideo/LazyVideo";

const transitionLine = { duration: 10, ease: "easeInOut" };

function MoppingAndVaccuming({ data }) {
  const ref1 = useRef();
  const ref2 = useRef();
  const robot1TrackInView = useInView(ref1, { once: true, amount: 0.3 });

  const controls = useAnimation();
  const pathControls = useAnimation();

  useEffect(() => {
    if (robot1TrackInView) {
      controls.start({ offsetDistance: "100%" });
      pathControls.start({ pathLength: 1 });
    }
    return () => {
      controls.stop();
      pathControls.stop();
    };
  }, [controls, robot1TrackInView]);

  return (
    <section className="w-full h-auto mt-[80px] lg:mt-[160px]">
      <div className="mx-[24px]">
        <div className="mb-[32px]">
          <Number numberBgColors={"#FFDFD0"} numberText={"01"} />
        </div>
        <div className={`text-center m-auto mb-[24px] max-w-[831px]`}>
          <h2>{data?.moppingAndVacuumingTitle}</h2>
        </div>
        <div className={`text-center m-auto max-w-[611px]`}>
          <p>Other robots sense. Matic <span className="font-bold">sees.</span></p>
          <p>Truly visual perception means Matic understands your home like you do.</p>
        </div>
      </div>

      {/* using aspect ratio */}
      <div className="lg:grid grid-cols-18 hidden">
        <div className="col-span-18">
          <div className="w-[100%] relative pt-[60px]">
            <div className="aspect-w-36 aspect-h-35">
              <div className="h-full w-full">
                <div className="h-full relative">
                  {/* Video Container 1 */}
                  <div
                    className={`w-[33.33%] absolute top-[2.9%] right-[16%] bg-white`}
                  >
                      <div className="aspect-w-480 aspect-h-480 relative">
                        <LazyVideo
                          videoSrc="/videos/Home_Matic_Is_Quiet.mp4"
                          image={homeIsQuiet}
                          customClasses={`h-full w-full object-cover object-center rounded-[16px] `}
                        />
                      </div>
                  </div>
                  <div className={`absolute top-[0] left-[48%]`}>
                      <div className="w-[240px] h-[160px] flex justify-center items-center bg-rose rounded-[16px] backdrop-blur-[4px] shadow-[0_0_16px_0_rgba(0,0,0,0.1)]">
                        <h6 className={`md:flex hidden text-center p-[15px] `}>
                          Knows what to clean, and what to avoid.
                        </h6>
                      </div>
                  </div>

                  <div
                    className={`w-[38.8%] absolute left-[22.4%] top-[17.4%] -z-[1]`}
                    ref={ref1}
                  >
                    <div className="aspect-w-280 aspect-h-259 relative">
                      <svg
                        width="522"
                        height="564"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <motion.path
                          d="M621 3H131C60.3076 3 3 60.3076 3 131V563"
                          fill="transparent"
                          strokeWidth="6"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={pathControls}
                          transition={transitionLine}
                        />
                        <motion.path
                          d="M621 40H136C82.9807 40 40 82.9807 40 136V563"
                          fill="transparent"
                          strokeWidth="6"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={pathControls}
                          transition={transitionLine}
                        />
                        <motion.path
                          d="M621 76H140C105.206 76 77 104.206 77 139V563"
                          fill="transparent"
                          strokeWidth="6"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={pathControls}
                          transition={transitionLine}
                        />
                      </svg>
                      <motion.div
                        className={`maticRobotContainer w-[182px] h-[136px]`}
                        initial={{ offsetDistance: "0%" }}
                        animate={controls}
                        transition={transitionLine}
                      >
                        <Image
                          src={`https:${data?.maticMovingOnTrackRobot?.fields?.file?.url}`}
                          className="w-[182px] h-[136px]"
                          alt="Moving Robot"
                          width="182"
                          height="136"
                        />
                      </motion.div>
                    </div>
                  </div>
                  {/* Video Container 2 */}
                  <div
                    className={`w-[38.88%] absolute top-[37.2%] left-[5.5%] bg-white`}
                  >
                      <div className="aspect-w-560 aspect-h-560">
                        <LazyVideo
                          videoSrc="/videos/works-on-all-home-surfaces-v1.mp4"
                          image={homeCleaningMacros}
                          customClasses={`h-full w-full object-cover object-center rounded-[16px] `}
                        />
                      </div>
                  </div>

                  <div className={`absolute left-[2.75%] top-[34.3%]`}>
                      <div className="w-[240px] h-[160px] flex justify-center items-center bg-rose rounded-[16px] backdrop-blur-[4px] shadow-[0_0_16px_0_rgba(0,0,0,0.1)]">
                        <h6
                          className={`md:flex hidden text-center py-[46px] px-[32px]`}
                        >
                          Works on all home surfaces. (even shag rugs)
                        </h6>
                      </div>
                  </div>

                  <div
                    className={`w-[38.88%] absolute right-[18%] top-[46%] -z-[1]`}
                    ref={ref2}
                  >
                    {/* <div className="aspect-w-280 aspect-h-259">
                      <Image
                        priority
                        src={`https:${data?.trackConnector1?.fields?.file?.url}`}
                        layout="fill"
                        alt="Connector 2"
                        className="custom-img"
                      />
                    </div> */}
                    <div className="aspect-w-564 aspect-h-521 relative">
                      <svg
                        width="564"
                        height="521"
                        viewBox="0 0 564 521"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0.5 76.125L424.621 76.1257C459.185 76.1257 487.313 104.228 487.313 138.762L487.313 520.7"
                          stroke="#ECEAE3"
                          strokeWidth="6"
                          strokeMiterlimit="10"
                        />
                        <path
                          d="M560.5 516.7L560.5 130.747C560.5 60.3122 503.137 3.00052 432.623 3.00052L0 3.00001"
                          stroke="#ECEAE3"
                          strokeWidth="6"
                          strokeMiterlimit="10"
                        />
                        <path
                          d="M0 39.5625L428.622 39.5641C481.243 39.5641 523.906 82.1897 523.906 134.763L523.906 519.701"
                          stroke="#ECEAE3"
                          strokeWidth="6"
                          strokeMiterlimit="10"
                        />
                      </svg>
                      {/* <svg
                        width="546"
                        height="505"
                        viewBox="0 0 546 505"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <motion.path
                          d="M0 3H412C484.35 3 543 61.6507 543 134V505"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 1 }}
                          animate={pathControls2}
                          transition={transitionLine}
                          strokeWidth="6"
                        />
                        <motion.path
                          d="M0.000488281 39.5H404.5C459.729 39.5 504.5 84.2715 504.5 139.5V505"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 1 }}
                          animate={pathControls2}
                          transition={transitionLine}
                          strokeWidth="6"
                        />
                        <motion.path
                          d="M0 77H402.501C437.295 77 465.501 105.206 465.501 140V505"
                          stroke="#ECEAE3"
                          strokeLinecap="round"
                          initial={{ pathLength: 1 }}
                          animate={pathControls2}
                          transition={transitionLine}
                          strokeWidth="6"
                        />
                      </svg>
                      <motion.div
                        className={`maticOnRightSideTrackRobot w-[182px] h-[136px] rotate-0`}
                        initial={{ offsetDistance: "0%" }}
                        animate={controls2}
                        transition={transitionLine}
                      >
                        <Image
                          src={`https:${data?.maticMovingOnTrackRobot?.fields?.file?.url}`}
                          className="w-[182px] h-[136px]"
                          alt="Moving Robot"
                          width="182"
                          height="136"
                        />
                      </motion.div> */}
                    </div>
                  </div>
                  {/* Video Container 3 */}
                  <div
                    className={`w-[38.88%] absolute right-[5.6%] top-[60%] bg-white`}
                  >
                      <div className="aspect-w-560 aspect-h-560 relative">
                        <LazyVideo
                          videoSrc="/videos/auto-toggles-between_mopping-and_vacuuming-video-v1.mp4"
                          image={moppingAndVacuuming}
                          customClasses={`h-full w-full object-cover object-center rounded-[16px] `}
                        />
                      </div>
                  </div>
                  <div className={`absolute top-[57.2%] left-[52.8%]`}>
                      <div className="w-[240px] h-[160px] flex justify-center items-center bg-rose rounded-[16px] backdrop-blur-[4px] shadow-[0_0_16px_0_rgba(0,0,0,0.1)]">
                        <h6 className={`md:flex hidden text-center p-[12px]`}>
                          {data?.autoTogglesBetweenMoppingAndVacuumingText}
                        </h6>
                      </div>
                  </div>
                  {/* furr image */}
                  <FurDetectionAnimation entryFields={data} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* flex for mobile*/}
      <div
        className={`flex flex-col justify-center items-center lg:hidden pt-[40px] h-full px-[24px] md:px-0`}
      >
        <div className="lg:hidden block mb-[80px] relative w-full max-w-[560px]">
            <div className="aspect-w-327 aspect-h-327">
              <LazyVideo
                videoSrc="/videos/Home_Matic_Is_Quiet.mp4"
                image={homeIsQuiet}
                customClasses={`h-full w-full object-cover object-center rounded-8`}
              />
            </div>
            <div
              className={`bg-rose rounded-[8px] w-[calc(100%-80px)] m-auto absolute -bottom-[4%] mob:-bottom-[14%] left-[40px]`}
            >
              <h6
                className={`flex text-center justify-center items-center px-[18px] lg:px-[35px] py-[12px]`}
              >
                Knows what to clean, and what to avoid.
              </h6>
            </div>
        </div>
        <div className="lg:hidden block mb-[80px] relative w-full max-w-[560px]">
            <div className="aspect-w-327 aspect-h-327">
              <LazyVideo
                videoSrc="/videos/works-on-all-home-surfaces-v1.mp4"
                image={homeCleaningMacros}
                customClasses={`h-full w-full object-cover object-center rounded-8`}
              />
            </div>
            <div
              className={`bg-rose rounded-[8px] w-[calc(100%-80px)] m-auto absolute -bottom-[4%] mob:-bottom-[11%] left-[40px]`}
            >
              <h6
                className={`flex text-center justify-center items-center px-[18px] lg:px-[35px] py-[12px]`}
              >
                Works on all home surfaces. (even shag rugs)
              </h6>
            </div>
        </div>
        <div className="lg:hidden block mb-[80px] relative w-full max-w-[560px]">
            <div className="aspect-w-327 aspect-h-327">
              <LazyVideo
                videoSrc="/videos/auto-toggles-between_mopping-and_vacuuming-video-v1.mp4"
                image={moppingAndVacuuming}
                customClasses={`h-full w-full object-cover object-center rounded-8`}
              />
            </div>
            <div
              className={`bg-rose rounded-[8px] w-[calc(100%-80px)] m-auto absolute -bottom-[7%] mob:-bottom-[11%] left-[40px]`}
            >
              <h6
                className={`flex text-center justify-center items-center px-[18px] lg:px-[35px] py-[12px]`}
              >
                {data?.autoTogglesBetweenMoppingAndVacuumingText}
              </h6>
            </div>
        </div>
      </div>
    </section>
  );
}

export default MoppingAndVaccuming;
